import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import styles from './custom-switch.module.scss';
import { ActiveType } from './custom-switch.types';

// The option string is also the value handed to `onSwitch`, so it stays as the caller wrote
// it. Only the label is translated; an option with no entry here renders as given.
const OPTION_LABELS = {
  Calls: 'switch.Calls',
  SMS: 'switch.SMS',
  Live: 'switch.Live',
  History: 'switch.History',
} as const;

const hasLabel = (option: string): option is keyof typeof OPTION_LABELS =>
  Object.prototype.hasOwnProperty.call(OPTION_LABELS, option);

interface CustomSwitchProps {
  initialActive: ActiveType;
  onSwitch: (active: ActiveType) => void;
  className?: string;
  options?: [ActiveType, ActiveType];
  variant?: 'default' | 'small';
}

export default function CustomSwitch({
  initialActive = 'Calls',
  onSwitch,
  className,
  options = ['Calls', 'SMS'],
  variant = 'default',
}: CustomSwitchProps) {
  const { t } = useTranslation('common');
  const [active, setActive] = useState<ActiveType>(initialActive);

  const handleSwitch = (type: ActiveType) => {
    setActive(type);
    if (onSwitch) {
      onSwitch(type);
    }
  };

  return (
    <div
      className={`${styles.switchContainer} ${variant === 'small' ? styles.small : ''} ${className ?? ''}`}
    >
      {options.map((option) => (
        <button
          key={option}
          type="button"
          className={`${styles.switchButton} ${active === option ? styles.active : styles.inactive}`}
          onClick={() => handleSwitch(option)}
        >
          {hasLabel(option) ? t(OPTION_LABELS[option]) : option}
        </button>
      ))}
    </div>
  );
}
